import SkillWizard from "../../Skills/SkillWizard";
import { useFeatureFlag, FLAGS } from "../../../config/featureFlags";
import React, { useEffect, useMemo, useState } from "react";
import { trackEvent } from "../../../utils/events";
import { authFetch, apiUrl } from "../../../utils/api";
import { HelpDot } from "../AgenticGlossary";

/**
 * SkillsTab — browse the factory's skill library, make skills your own.
 *
 * FAST: the factory ships with proven skills (templates, read-only); a
 * member forks any of them into a personal version that shadows the
 * built-in just for them, edits it to fit their business, or writes new
 * ones from scratch. Ollie/Harry/Mako/Orca all read the same library, so
 * a skill saved here changes how every surface works on the next turn.
 *
 * Deliberate: no export-all. Skills open one at a time (server enforces a
 * daily distinct-read cap on built-ins) — the library is for USING, not
 * bulk-downloading.
 */
const SkillsTab = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // { skill, custom } from GET /:slug
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "", content: "" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [teams, setTeams] = useState([]); // my teams (share targets) — empty when team_workspaces is off
  const [shareBusy, setShareBusy] = useState(false);
  const builderOn = useFeatureFlag(FLAGS.CUSTOM_SKILL_BUILDER);
  const teamSharingOn = useFeatureFlag(FLAGS.SKILL_TEAM_SHARING);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(apiUrl("/api/skills"));
      if (!r.ok) {
        setSkills([]);
        return;
      }
      const d = await r.json();
      setSkills(d.skills || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (!teamSharingOn) return;
    (async () => {
      try {
        const r = await authFetch(apiUrl("/api/teams"));
        if (!r.ok) return; // team_workspaces off or no access — no share UI
        const d = await r.json();
        setTeams(d.teams || []);
      } catch (_) { /* no share UI without teams */ }
    })();
  }, [teamSharingOn]);

  // Yours first (labeled), then what your team shared with you, then the built-ins.
  const kindOf = (s) => (s.is_mine ? "mine" : s.is_team ? "team" : "builtin");
  const SECTIONS = [
    { key: "mine", title: "Your skills", hint: "Built by you or forked from a built-in — you can edit, share, and delete these." },
    { key: "team", title: "Shared with your team", hint: "A teammate owns these. Run them as-is, or \"Make it mine\" to get your own editable copy." },
    { key: "builtin", title: "Built-in", hint: "The factory library. Fork any of them to make it yours." },
  ];

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    if (!needle) return skills;
    return skills.filter((s) =>
      `${s.slug} ${s.name} ${s.description || ""}`.toLowerCase().includes(needle),
    );
  }, [skills, q]);

  const openSkill = async (slug) => {
    trackEvent("skill_opened", { slug });
    setBusy(true);
    setNotice("");
    setEditing(false);
    try {
      const r = await authFetch(apiUrl(`/api/skills/${slug}`));
      if (r.status === 429) {
        const d = await r.json().catch(() => ({}));
        setNotice(d.message || "Daily limit on opening built-in skills reached — resets tomorrow.");
        return;
      }
      if (!r.ok) return;
      const d = await r.json();
      setSelected(d);
      setDraft({
        name: d.skill.name || "",
        description: d.skill.description || "",
        content: d.skill.content || "",
      });
    } finally {
      setBusy(false);
    }
  };

  const forkSelected = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await authFetch(apiUrl(`/api/skills/${selected.skill.slug}/fork`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (r.ok) {
        setNotice("Forked — this is your copy now. Edit away; the built-in stays untouched for everyone else.");
        await openSkill(selected.skill.slug);
        setEditing(true);
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async (slug) => {
    setBusy(true);
    try {
      const r = await authFetch(apiUrl("/api/skills"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, ...draft }),
      });
      if (r.ok) {
        setNotice("Saved — live for your agents on every surface from the next turn.");
        setEditing(false);
        await openSkill(slug);
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const deleteCustom = async (slug) => {
    if (!window.confirm("Delete your custom version? If it shadows a built-in, the built-in comes back.")) return;
    setBusy(true);
    try {
      await authFetch(apiUrl(`/api/skills/${slug}`), { method: "DELETE" });
      setSelected(null);
      setNotice("Deleted.");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const newSkill = () => {
    setSelected({ skill: { slug: "", name: "", description: "", content: "" }, custom: true, isNew: true });
    setDraft({ name: "", description: "", content: "# My Skill\n\nWhen to use this:\n\nSteps:\n1. " });
    setEditing(true);
  };

  // kind: "mine" | "team" | "builtin" (a boolean still means yours/built-in for older callers)
  const badge = (kind) => {
    const k = kind === true ? "mine" : kind === false ? "builtin" : kind;
    const palette = {
      mine: { bg: "rgba(16,185,129,0.15)", fg: "#10b981", label: "Yours" },
      team: { bg: "rgba(59,130,246,0.15)", fg: "#60a5fa", label: "Team" },
      builtin: { bg: "rgba(124,58,237,0.15)", fg: "#a78bfa", label: "Built-in" },
    }[k] || { bg: "rgba(124,58,237,0.15)", fg: "#a78bfa", label: "Built-in" };
    return (
      <span data-testid={`skill-badge-${k}`} style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "999px", background: palette.bg, color: palette.fg }}>
        {palette.label}
      </span>
    );
  };

  const shareSkill = async (slug, teamId) => {
    setShareBusy(true);
    try {
      const r = await authFetch(apiUrl(`/api/skills/${slug}/share`), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ teamId }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { trackEvent("skill_shared", { slug }); setNotice("Shared — your teammates can run it now (read-only for them; they can fork their own copy)."); await openSkill(slug); await load(); }
      else setNotice(d.error || "Couldn't share that skill.");
    } finally { setShareBusy(false); }
  };
  const unshareSkill = async (slug) => {
    setShareBusy(true);
    try {
      const r = await authFetch(apiUrl(`/api/skills/${slug}/unshare`), { method: "POST" });
      if (r.ok) { setNotice("No longer shared — it's just yours again."); await openSkill(slug); await load(); }
    } finally { setShareBusy(false); }
  };

  const btn = (primary) => ({
    background: primary ? "var(--accent-primary, #7c3aed)" : "transparent",
    color: primary ? "#fff" : "var(--text-secondary)",
    border: primary ? "none" : "1px solid var(--border-color, rgba(255,255,255,0.15))",
    borderRadius: "8px",
    padding: "8px 14px",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  });

  // ── Detail view ─────────────────────────────────────────────────────
  if (selected) {
    const s = selected.skill;
    const slugForSave = selected.isNew
      ? (draft.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80)
      : s.slug;
    return (
      <div>
        <button onClick={() => { setSelected(null); setNotice(""); }} style={{ ...btn(false), marginBottom: "16px" }}>
          ← All skills
        </button>
        {notice && (
          <p style={{ color: "#10b981", fontSize: "13px", margin: "0 0 12px" }}>{notice}</p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
          {editing ? (
            <input
              value={draft.name}
              placeholder="Skill name"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              style={{ fontSize: "18px", fontWeight: 700, flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-color, rgba(255,255,255,0.15))", borderRadius: "8px", padding: "8px 10px", color: "var(--text-primary)" }}
            />
          ) : (
            <h3 style={{ margin: 0 }}>{s.name}</h3>
          )}
          {!selected.isNew && badge(selected.mine ? "mine" : selected.shared ? "team" : "builtin")}
        </div>
        {!selected.isNew && (
          <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: "0 0 12px" }}>
            {s.slug}
            {selected.shared && (
              <span data-testid="skill-shared-by">
                {" · "}Shared by {selected.shared_by || "a teammate"} · view only
              </span>
            )}
            {selected.custom && (
              <span data-testid="skill-run-hint">
                {" · "}Run it: type <code>/{s.slug}</code> in chat or Telegram
                {s.version ? ` · version ${s.version}` : ""}
                {s.lint_score != null ? ` · quality ${s.lint_score}/100` : ""}
                {s.use_count ? ` · used ${s.use_count}×` : ""}
              </span>
            )}
          </p>
        )}
        {editing ? (
          <>
            <input
              value={draft.description}
              placeholder="One-line: when should your agents use this?"
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-color, rgba(255,255,255,0.15))", borderRadius: "8px", padding: "8px 10px", color: "var(--text-primary)", fontSize: "13px", marginBottom: "10px" }}
            />
            <textarea
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              rows={18}
              style={{ width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: "13px", background: "rgba(0,0,0,0.3)", border: "1px solid var(--border-color, rgba(255,255,255,0.15))", borderRadius: "8px", padding: "12px", color: "var(--text-primary)" }}
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
              <button disabled={busy || !draft.name || !draft.content} onClick={() => saveDraft(slugForSave)} style={btn(true)}>
                {busy ? "…" : "Save skill"}
              </button>
              <button onClick={() => (selected.isNew ? setSelected(null) : setEditing(false))} style={btn(false)}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {s.description && (
              <p style={{ color: "var(--text-secondary)", fontSize: "14px", margin: "0 0 14px" }}>{s.description}</p>
            )}
            <pre
              style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "13px", lineHeight: 1.6, background: "rgba(0,0,0,0.3)", border: "1px solid var(--border-color, rgba(255,255,255,0.1))", borderRadius: "8px", padding: "16px", maxHeight: "420px", overflowY: "auto", color: "var(--text-primary)" }}
            >
              {s.content || "(empty)"}
            </pre>
            {selected.custom && (selected.runs || []).length > 0 && (
              <div style={{ marginTop: "14px" }} data-testid="skill-runs">
                <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px" }}>🔁 What recent runs taught us</div>
                {(selected.runs || []).slice(0, 5).map((r) => (
                  <div key={r.id} style={{ fontSize: "12px", color: "var(--text-secondary)", padding: "8px 10px", marginBottom: "6px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-color, rgba(255,255,255,0.08))", borderRadius: "8px" }}>
                    <div><b style={{ color: "var(--text-primary)" }}>{String(r.created_at).slice(0, 10)} · {r.outcome}</b> — worked: {r.worked} · didn't: {r.did_not}</div>
                    {r.proposed_change && (
                      <div style={{ marginTop: "4px" }}>
                        {r.status === "applied" ? "✅ Applied rule: " : r.status === "dismissed" ? "✖ Declined rule: " : "💡 Proposed rule: "}
                        <span style={{ color: "var(--text-primary)" }}>{r.proposed_change}</span>
                        {r.proposal_kind === "tool_candidate" && <span style={{ marginLeft: 6, fontSize: "11px", padding: "1px 6px", borderRadius: 999, background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>tool candidate — should become code</span>}
                        {r.status === "open" && (
                          <span style={{ marginLeft: 8 }}>
                            Tell Ollie "apply the proposed rule to /{s.slug}" to fold it in, or{" "}
                            <button onClick={async () => { await authFetch(apiUrl(`/api/skills/${s.slug}/runs/${r.id}/dismiss`), { method: "POST" }); openSkill(s.slug); }} style={{ ...btn(false), padding: "2px 8px", fontSize: "11px" }}>decline</button>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "12px", flexWrap: "wrap", alignItems: "center" }}>
              {selected.mine ? (
                <>
                  <button onClick={() => setEditing(true)} style={btn(true)}>Edit</button>
                  <button onClick={() => deleteCustom(s.slug)} disabled={busy} style={{ ...btn(false), color: "#ef4444" }}>
                    Delete my version
                  </button>
                  {teamSharingOn && teams.length > 0 && (
                    selected.team_id ? (
                      <span data-testid="skill-share-state" style={{ fontSize: "12px", color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 8 }}>
                        👥 Shared with {(teams.find((t) => t.id === selected.team_id) || {}).name || "your team"}
                        <button onClick={() => unshareSkill(s.slug)} disabled={shareBusy} style={{ ...btn(false), padding: "4px 10px", fontSize: "12px" }} data-testid="skill-unshare">
                          {shareBusy ? "…" : "Stop sharing"}
                        </button>
                      </span>
                    ) : teams.length === 1 ? (
                      <button onClick={() => shareSkill(s.slug, teams[0].id)} disabled={shareBusy} style={btn(false)} data-testid="skill-share" title={`Share read-only with ${teams[0].name}`}>
                        {shareBusy ? "…" : `👥 Share with ${teams[0].name}`}
                      </button>
                    ) : (
                      <select value="" disabled={shareBusy} onChange={(e) => e.target.value && shareSkill(s.slug, e.target.value)} data-testid="skill-share" title="Share read-only with one of your teams" style={{ ...btn(false), padding: "6px 10px" }}>
                        <option value="">👥 Share with team…</option>
                        {teams.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                      </select>
                    )
                  )}
                </>
              ) : (
                <button onClick={forkSelected} disabled={busy} style={btn(true)} data-testid="skill-fork">
                  {busy ? "…" : "Make it mine"}
                </button>
              )}
            </div>
            {!selected.mine && (
              <p style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "8px" }}>
                {selected.shared
                  ? `"Make it mine" copies this shared skill into your personal library — your copy then takes over for your agents, and you can tune it. ${selected.shared_by || "Your teammate"}'s version stays as they wrote it.`
                  : `"Make it mine" copies this into your personal library — your version then takes over for your agents, and you can tune it to your business. The built-in stays untouched.`}
              </p>
            )}
            {selected.mine && teamSharingOn && teams.length === 0 && (
              <p style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "8px" }}>
                Want teammates to run this too? Add them under Settings → Team, then share it from here.
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Guided builder (modal) ──────────────────────────────────────────
  if (wizardOpen) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", overflowY: "auto", padding: "24px 12px" }} data-testid="skill-wizard-modal">
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <SkillWizard
            onCancel={() => setWizardOpen(false)}
            onComplete={async ({ slug, lint }) => {
              setWizardOpen(false);
              setNotice(`Saved — run it any time with /${slug}${lint?.score != null ? ` (quality ${lint.score}/100)` : ""}.`);
              await load();
              openSkill(slug);
            }}
          />
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────
  return (
    <div>
      <div className="setting-group" style={{ marginBottom: "20px" }}>
        <h3>🧰 Skills — your factory's playbooks<HelpDot term="skill" label="What are skills, slash commands, run notes and team sharing?" /></h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px", lineHeight: 1.6 }}>
          Every agent — Ollie here, Mako in Telegram, Orca on your desktop, Harry in the background —
          checks this library before acting and follows the matching playbook. Fork any built-in to make
          it yours, build your own with the guided builder (or just tell Ollie "make this a skill"), and run any skill by typing /its-name in chat or Telegram. Skills are living documents: after each run your agent reports what worked, proposes a rule for any edge case, and changes nothing without your yes.
        </p>
        <p style={{ color: "var(--text-secondary)", fontSize: "13px", lineHeight: 1.6, marginTop: "8px" }}>
          Each skill leads with who can run it: 🦦 Ollie · 🦡 Harry · 🦈 Mako · 🐋 Orca.
          🦦🦡🦈🐋 runs anywhere; 🐋 alone means it needs your desktop's local tools — ask Orca for those.
        </p>
      </div>
      {notice && <p style={{ color: "#f59e0b", fontSize: "13px" }}>{notice}</p>}
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${skills.length} skills…`}
          style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-color, rgba(255,255,255,0.15))", borderRadius: "8px", padding: "10px 12px", color: "var(--text-primary)", fontSize: "14px" }}
        />
        {builderOn && (
          <button onClick={() => setWizardOpen(true)} style={btn(true)} data-testid="build-skill-btn">✨ Build a skill</button>
        )}
        <button onClick={newSkill} style={btn(false)}>+ Blank skill</button>
      </div>
      {loading ? (
        <p style={{ color: "var(--text-secondary)" }}>Loading the library…</p>
      ) : (
        <div style={{ maxHeight: "480px", overflowY: "auto" }}>
          {SECTIONS.map((sec) => {
            const items = filtered.filter((s) => kindOf(s) === sec.key);
            if (!items.length) return null;
            return (
              <div key={sec.key} data-testid={`skills-section-${sec.key}`} style={{ marginBottom: "14px" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: "8px", margin: "4px 0 8px" }}>
                  <span style={{ fontWeight: 700, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)" }}>{sec.title}</span>
                  <span style={{ fontSize: "12px", color: "var(--text-secondary)", opacity: 0.8 }}>{items.length} · {sec.hint}</span>
                </div>
                {items.map((s) => (
                  <button
                    key={s.slug}
                    onClick={() => openSkill(s.slug)}
                    style={{ display: "flex", width: "100%", textAlign: "left", alignItems: "center", gap: "12px", padding: "10px 12px", marginBottom: "6px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-color, rgba(255,255,255,0.08))", borderRadius: "8px", cursor: "pointer", color: "var(--text-primary)" }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "14px" }}>{s.name}</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.is_team && s.shared_by ? `Shared by ${s.shared_by} · ` : ""}{s.description || s.slug}
                      </div>
                    </div>
                    {badge(kindOf(s))}
                  </button>
                ))}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Nothing matches "{q}" — try another word, or create it as a new skill.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillsTab;
